import type { Keyword } from "@/types";
import type { KeywordRanking } from "@/types/landscape";
import type { HeroKeywordsSortField } from "@/types/product";

/**
 * AUT-426: the landscape's keyword shape, expressed as the shape the shared keyword table reads.
 *
 * The two screens show the same columns and had two independent tables, which drifted four separate
 * times. Converging them needs a translation, because the landscape API and the keyword-bank API
 * name the same quantities differently.
 *
 * **`exactVol` is the volume; `search_volume` is the trend.** That is not a typo on either side --
 * `KeywordRanking.exactVol` feeds the "Search Volume" column and `KeywordRanking.search_volume`
 * feeds "Trend" as a percentage (`UnifiedLandscapeTable`'s own headers sort on exactly that). Map
 * them by name rather than by meaning and the two columns silently swap.
 */
/**
 * The sibling ASIN behind a keyword's hero rank, or null. The payload's two fields describe
 * `ranks[0]`, so they only apply where the displayed rank is `ranks[0]` (`heroIndex === 0`).
 */
export function heroRankSiblingAsin(ranking: KeywordRanking, heroIndex: number): string | null {
  if (heroIndex !== 0 || ranking.hero_rank_from_sibling !== true) return null;
  const asin = ranking.hero_rank_asin?.trim() ?? "";
  return asin === "" ? null : asin;
}

export function landscapeKeywordToBankKeyword(
  ranking: KeywordRanking,
  options: { productId: number; heroIndex: number },
): Keyword {
  const heroRank = ranking.ranks?.[options.heroIndex] ?? null;
  const siblingAsin = heroRankSiblingAsin(ranking, options.heroIndex);

  return {
    id: Number(ranking.id),
    search: ranking.keyword,
    product_id: options.productId,

    rank: heroRank,
    hero_rank: heroRank,
    ranks: ranking.ranks,
    hero_rank_asin: siblingAsin,
    hero_rank_from_sibling: siblingAsin !== null,

    search_volume: ranking.exactVol ?? null,
    search_volume_trend: ranking.search_volume == null ? null : String(ranking.search_volume),

    kps: ranking.kpg,
    keyword_sales: ranking.keyword_sales,
    asin_density: ranking.asin_density,
    competitors: ranking.competitors,
    hero_relevance: ranking.hero_relevance,
    ppc_max_bid: ranking.ppc_max_bid,
    is_master_list: ranking.is_master_list ?? false,

    // Not carried by the landscape payload. Null rather than invented: the shared cells render a
    // dash for an absent metric, which is the honest reading of "the landscape does not report it".
    landscape_key: null,
    landscape_id: null,
    created_at: null,
    updated_at: null,
    ppc: null,
    ppc_min_bid: null,
    kps_raw: null,
    kps_search_score: null,
    kps_search_trend_score: null,
    kps_hero_rank_score: null,
    kps_hero_relevance_score: null,
    includes_hero_brand: false,
    includes_competitor_brand: false,
    no_overlap_with_hero_content: false,

    // AUT-496. Mapped explicitly: this object ends in `as Keyword`, so a field left out is simply
    // `undefined` at runtime and the cast keeps the compiler quiet about it.
    serp_match_pct: ranking.serp_match_pct ?? null,
    serp_match_organic_pct: ranking.serp_match_organic_pct ?? null,
    serp_match_sponsored_pct: ranking.serp_match_sponsored_pct ?? null,
    serp_match_scored: ranking.serp_match_scored ?? null,
    serp_match_found: ranking.serp_match_found ?? null,
    serp_match_week: ranking.serp_match_week ?? null,
  } as Keyword;
}

/** The landscape's own sort vocabulary, as `UnifiedLandscapeTable` declares it. */
export type LandscapeSortField =
  | "keyword" | "heroRank" | "kpg" | "keyword_sales" | "exactVol"
  | "asin_density" | "competitors" | "hero_relevance" | "search_volume" | "serp_match_pct";

/**
 * The same columns, named differently on each side. Kept as one map rather than two switch
 * statements so a column cannot be added to one direction and forgotten in the other.
 */
const SORT_FIELDS: ReadonlyArray<readonly [LandscapeSortField, HeroKeywordsSortField]> = [
  ["keyword", "keyword"],
  ["heroRank", "heroRank"],
  ["kpg", "kpg"],
  ["keyword_sales", "keywordSales"],
  ["exactVol", "exactVolume"],
  ["asin_density", "asinDensity"],
  ["competitors", "competition"],
  ["hero_relevance", "heroRelevanceScore"],
  ["search_volume", "searchVolumeTrend"],
  ["serp_match_pct", "serpMatch"],
];

export function toBankSortField(field: LandscapeSortField): HeroKeywordsSortField {
  return SORT_FIELDS.find(([landscape]) => landscape === field)?.[1] ?? "kpg";
}

export function toLandscapeSortField(field: HeroKeywordsSortField): LandscapeSortField | null {
  return SORT_FIELDS.find(([, bank]) => bank === field)?.[0] ?? null;
}
